import { useEffect, useRef, useState } from 'react';
import arabesqueAi from '@aether/ui/app-shared/assets/arabesque-ai.svg';
import { ButtonNew } from '@aether/ui/ButtonNew';
import { Icon } from '@aether/ui/Icon';
import { cn } from '@aether/ui/utils';
import { getAppOrigin } from '@aether/utils/services/linking-service';
import { Link, useLocation } from 'react-router-dom';

import { Theme } from '~/root';

const NAV_ITEMS = [
  { label: 'Get Started', path: '/get-started' },
  { label: 'Our Approach', path: '/our-approach' },
  { label: 'Insights', path: '/insights' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'About', path: '/about-us' },
];

export function NewHeader({
  origin = '',
  isAuthenticated,
  currentTheme = 'light',
  onThemeChange,
}: {
  origin: string | null;
  isAuthenticated: boolean;
  currentTheme: Theme;
  onThemeChange: (theme: Theme) => void;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  if (!origin) origin = '';

  const currentSection = NAV_ITEMS.find(item => 
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  )?.label || '';

  const toggleTheme = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    if (onThemeChange) {
      onThemeChange(newTheme);
    }
  };

  return (
    <nav className="fixed z-10 w-full border-b border-gray-200 bg-white/80 py-4 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/80">
      <div className="container mx-auto flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-2xl font-bold shrink-0">
            <img
              src={
                currentTheme === 'light' ? '/images/home/1. arabesque_2020_b.png' : '/images/home/2. arabesque_2020_w.png'
              }
              alt="Arabesque"
              className="h-12 max-h-[4rem] w-auto sm:h-16 md:h-24"
            />
          </Link>
          {currentSection && (
            <span className="text-sm block truncate font-medium text-gray-600 dark:text-gray-400 lg:hidden">
              / {currentSection}
            </span>
          )}
        </div>

        {/* Desktop Navigation */}
        <div className="hidden space-x-8 lg:flex">
          {NAV_ITEMS.map(item => (
            <Link 
              key={item.path} 
              to={item.path} 
              className="hover:text-gray-600 dark:hover:text-gray-400"
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-end gap-4">
          <UserMenu isAuthenticated={isAuthenticated} origin={origin} />
          <Icon
            role="button"
            icon={currentTheme === 'dark' ? 'ic:outline-light-mode' : 'ic:outline-dark-mode'}
            onClick={toggleTheme}
            className="hover:text-gray-600 dark:hover:text-gray-400"
          />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden"
            aria-label="Toggle menu"
          >
            <Icon
              icon={isMobileMenuOpen ? 'ic:outline-close' : 'ic:outline-menu'}
              className="h-6 w-6 hover:text-gray-600 dark:hover:text-gray-400"
            />
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          'absolute left-0 right-0 top-full border-b border-gray-200 bg-white/95 backdrop-blur-sm transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-950/95 lg:hidden',
          isMobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
        )}
      >
        <div className="container mx-auto space-y-4 px-6 py-4">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className="block py-2 hover:text-gray-600 dark:hover:text-gray-400"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <div className="border-t border-gray-200 pt-4 dark:border-gray-800">
            <UserMenuMobile isAuthenticated={isAuthenticated} origin={origin} onClose={() => setIsMobileMenuOpen(false)} />
          </div>
        </div>
      </div>
    </nav>
  );
}

function UserMenu({ isAuthenticated, origin }: { isAuthenticated: boolean; origin: string }) {
  const appHost = getAppOrigin(origin) || '';

  if (!isAuthenticated)
    return (
      <div className="hidden space-x-4 lg:flex">
        <ButtonNew variant="primary" iconBefore="ic:outline-add" className="dark:bg-portfolio-900" asChild>
          <Link to={`${appHost}/auth/register`}>Create Account</Link>
        </ButtonNew>
        <ButtonNew
          variant="secondary"
          iconBefore="ic:outline-edit"
          className="dark:border-portfolio-900 dark:text-white"
          asChild
        >
          <Link to={`${appHost}/auth/login`}>Sign In</Link>
        </ButtonNew>
      </div>
    );

  return (
    <ButtonNew variant="primary" iconAfter="ic:outline-arrow-forward" className="dark:bg-portfolio-900" asChild>
      <Link to={appHost}>App</Link>
    </ButtonNew>
  );
}

function UserMenuMobile({ isAuthenticated, origin, onClose }: { isAuthenticated: boolean; origin: string; onClose: () => void }) {
  const appHost = getAppOrigin(origin) || '';

  if (!isAuthenticated)
    return (
      <div className="flex flex-col space-y-4">
        <ButtonNew variant="primary" iconBefore="ic:outline-add" className="dark:bg-portfolio-900" asChild>
          <Link to={`${appHost}/auth/register`} onClick={onClose}>Create Account</Link>
        </ButtonNew>
        <ButtonNew
          variant="secondary"
          iconBefore="ic:outline-edit"
          className="dark:border-portfolio-900 dark:text-white"
          asChild
        >
          <Link to={`${appHost}/auth/login`} onClick={onClose}>Sign In</Link>
        </ButtonNew>
      </div>
    );

  return (
    <ButtonNew variant="primary" iconAfter="ic:outline-arrow-forward" className="dark:bg-portfolio-900" asChild>
      <Link to={appHost} onClick={onClose}>App</Link>
    </ButtonNew>
  );
}
